import { Module } from "../lib/modules";
import { moduleRegistry } from "../lib/registry";
import { StateManager } from "../lib/stateManager";
import sleep from "../utils/sleep";

interface HomeState {
  likes: Set<string>; // string is article slug
  offset: number;
  headlineID: number;
}

export const Home: Module<HomeState> = () => {
  // Create a state manager with initial state
  const state = new StateManager<HomeState>({
    likes: new Set<string>(),
    offset: 0,
    headlineID: 0
  });

  const toggleLike = (slug: string) => {
    const likes = state.get("likes");
    if (likes.has(slug)) {
      likes.delete(slug);
    } else {
      likes.add(slug);
    }
    state.set("likes", likes);
  };

  // Handle like button click
  const handleLikeClick = (slug: string) => {
    console.log("Like button clicked for slug:", slug);
    toggleLike(slug);
  };

  const handleCopyClick = async (slug: string, x: number, y: number) => {
    // Await so that the copy text is shown after allowing clipboard write
    await window.navigator.clipboard.writeText("https://videogamesmademe.com/article/" + slug);

    const copyText = document.createElement("span");
    copyText.innerText = "Copied!";
    copyText.classList.add("home__article-copy-text");
    copyText.style.top = `${y}px`;
    copyText.style.left = `${x}px`;

    document.body.appendChild(copyText);

    await sleep(10);
    copyText.style.top = `${y - 130}px`;
    copyText.style.opacity = "0";
    await sleep(2000); // Should be as long as the CSS transition
    copyText.remove();
  };

  // Initialize the carousel
  const initCarousel = () => {
    const carousel = document.querySelector(".js-carousel-wrapper") as HTMLDivElement;
    const prevButton = document.querySelector(".js-carousel-prev") as HTMLButtonElement;
    const nextButton = document.querySelector(".js-carousel-next") as HTMLButtonElement;
    const indicatorsContainer = document.querySelector(".js-carousel-indicators") as HTMLDivElement;

    if (!carousel || !prevButton || !nextButton || !indicatorsContainer) return;

    const slides = carousel.querySelectorAll(".home__carousel-slide");
    if (slides.length === 0) return;

    let currentIndex = 0;
    // Make itemsPerSlide variable changeable
    let itemsPerSlide = window.innerWidth >= 1000 ? 2 : 1;
    let maxIndex = Math.max(0, Math.ceil(slides.length / itemsPerSlide) - 1);

    // Create indicators
    const createIndicators = () => {
      indicatorsContainer.innerHTML = "";
      for (let i = 0; i <= maxIndex; i++) {
        const indicator = document.createElement("div");
        indicator.classList.add("home__carousel-indicator");
        if (i === currentIndex) indicator.classList.add("active");
        indicator.addEventListener("click", () => {
          goToSlide(i);
        });
        indicatorsContainer.appendChild(indicator);
      }
    };

    createIndicators();

    // Function to go to a specific slide
    const goToSlide = (index: number) => {
      currentIndex = index;

      // Update slide position
      const offset = index * (100 / itemsPerSlide) * itemsPerSlide;
      carousel.style.transform = `translateX(-${offset}%)`;

      // Update indicators
      const indicators = indicatorsContainer.querySelectorAll(".home__carousel-indicator");
      indicators.forEach((ind, i) => {
        if (i === index) {
          ind.classList.add("active");
        } else {
          ind.classList.remove("active");
        }
      });

      // Update button states
      prevButton.disabled = index === 0;
      nextButton.disabled = index === maxIndex;
    };

    // Setup event listeners for buttons
    prevButton.addEventListener("click", () => {
      goToSlide(Math.max(0, currentIndex - 1));
    });

    nextButton.addEventListener("click", () => {
      goToSlide(Math.min(maxIndex, currentIndex + 1));
    });

    // Handle window resize
    const handleResize = () => {
      const newItemsPerSlide = window.innerWidth >= 1000 ? 2 : 1;
      if (newItemsPerSlide !== itemsPerSlide) {
        // Update the itemsPerSlide variable
        itemsPerSlide = newItemsPerSlide;

        // Recalculate maxIndex
        maxIndex = Math.max(0, Math.ceil(slides.length / itemsPerSlide) - 1);

        // Make sure currentIndex is valid for the new layout
        if (currentIndex > maxIndex) {
          currentIndex = maxIndex;
        }

        // Recreate indicators
        createIndicators();

        // Reposition the carousel
        goToSlide(currentIndex);
      }
    };

    // Debounce the resize handler
    let resizeTimeout: number | null = null;
    window.addEventListener("resize", () => {
      if (resizeTimeout) {
        window.clearTimeout(resizeTimeout);
      }

      resizeTimeout = window.setTimeout(() => {
        handleResize();
        resizeTimeout = null;
      }, 250); // 250ms debounce time
    });

    // Initialize button states
    prevButton.disabled = true;
    nextButton.disabled = maxIndex === 0;
  };

  const init = () => {
    attachEventListeners();

    // Initialize the editorial picks carousel
    initCarousel();

    const headline = document.querySelector(".home__headline") as HTMLDivElement;
    if (headline) {
      if (headline.dataset.id) {
        const headlineID = headline.dataset.id;
        state.set("headlineID", parseInt(headlineID, 10));
      }
    }

    const articleCards = document.querySelectorAll(".home__article-card");
    for (const c of articleCards) {
      const card = c as HTMLDivElement;

      // Like button
      const likeButton = card.querySelector(".home__article-like") as HTMLButtonElement;
      if (!likeButton) continue;
      likeButton.addEventListener("click", () => {
        handleLikeClick(card.dataset.id as string);
      });

      // Copy button
      const copyButton = card.querySelector(".home__article-share") as HTMLButtonElement;
      if (!copyButton) continue;
      copyButton.addEventListener("click", (e) => {
        // Get the scroll position
        const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
        const scrollY = window.pageYOffset || document.documentElement.scrollTop;

        // Position the element at the click coordinates, adjusted for scroll
        const x = e.clientX + scrollX;
        const y = e.clientY + scrollY;

        handleCopyClick(card.dataset.id as string, x, y);
      });
    }

    state.set("offset", 9);

    const likes = window.localStorage.getItem("likes");
    if (likes) {
      const parsedLikes = JSON.parse(likes) as string[];
      state.set("likes", new Set(parsedLikes));
    }

    const articleContainer = document.querySelector(".home__articles");
    if (articleContainer) {
      // Only hide load more if we have significantly fewer articles than expected
      // This accounts for the headline removal scenario
      if (articleContainer.children.length < 8) {
        setNoMoreArticles();
      }
    }
  };

  const setNoMoreArticles = () => {
    const loadMoreButton = document.querySelector(".home__load-more") as HTMLDivElement;
    loadMoreButton.classList.add("home__load-more--hidden");
    loadMoreButton.removeEventListener("click", handleLoadMore);
    loadMoreButton.querySelector("button")?.remove();
    const noMoreText = document.createElement("span");
    noMoreText.innerText = "No more articles to load";
    noMoreText.classList.add("home__load-more--no-more");
    loadMoreButton.appendChild(noMoreText);
    state.set("offset", state.get("offset") + 10); // Increment offset to avoid reloading
  };

  const handleLoadMore = async () => {
    try {
      const res = await fetch(
        `/api/more_latest_articles?offset=${state.get("offset")}&limit=9&headline=${state.get("headlineID")}`
      );

      if (!res.ok) {
        throw new Error("Failed to load more articles");
      }

      const data = await res.text();

      if (!data || data.trim() === "") {
        console.warn("No more articles to load");
        return setNoMoreArticles();
      }

      // Run skeleton module on new images
      let skeleton = (_container: Element) => {};
      const skeletonModule = moduleRegistry.get("imageSkeleton");
      if (skeletonModule) {
        skeleton = skeletonModule.instance.state.get("skeleton");
      }

      const articlesContainer = document.querySelector(".home__articles") as HTMLDivElement;
      const parser = new DOMParser();
      const doc = parser.parseFromString(data, "text/html");
      const newArticles = doc.querySelectorAll(".home__article-card");
      for (const article of newArticles) {
        const articleCard = article as HTMLDivElement;
        const articleCardAnchor = articleCard.querySelector("a") as HTMLAnchorElement;
        articlesContainer.appendChild(articleCard);

        skeleton(articleCardAnchor);
      }

      // Update offset
      const newOffset = state.get("offset") + newArticles.length;
      if (newArticles.length < 9) {
        return setNoMoreArticles();
      }
      state.set("offset", newOffset);
    } catch (error) {
      console.error("Error loading more articles:", error);
    }
  };

  const attachEventListeners = () => {
    const loadMoreButton = document.querySelector(".home__load-more > button") as HTMLButtonElement;
    loadMoreButton.addEventListener("click", handleLoadMore);
  };

  const detachEventListeners = () => {};

  const destroy = () => {
    detachEventListeners();
    // Other cleanup
  };

  // Set up theme change observers
  state.onChange("likes", (newState) => {
    window.localStorage.setItem("likes", JSON.stringify(Array.from(newState)));
    const articleCards = document.querySelectorAll(".home__article-card");
    for (const c of articleCards) {
      const card = c as HTMLDivElement;
      const likeButton = card.querySelector(".home__article-like") as HTMLButtonElement;
      if (!likeButton) continue;
      const slug = card.dataset.id as string;
      if (newState.has(slug)) {
        likeButton.classList.add("home__article-like--active");
      } else {
        likeButton.classList.remove("home__article-like--active");
      }
    }
  });

  state.onChange("offset", () => {
    const loadMoreButton = document.querySelector(".home__load-more") as HTMLButtonElement;
    // Don't hide load more button based on offset modulo since offset starts at 10
    // The button visibility should be controlled by the actual article loading logic
    loadMoreButton.classList.remove("home__load-more--hidden");
  });

  return {
    init,
    attachEventListeners,
    detachEventListeners,
    destroy,
    state,
    pages: ["/"],
    name: "home"
  };
};
